import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Chip,
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import RefreshIcon from "@mui/icons-material/Refresh";
import moment from "moment";
import { useLazyGetRoadClosureMapDataQuery } from "store/services/roadClosureApi";

// Center of Nepal
const NEPAL_CENTER = [28.3949, 84.1240];

const buildClosureIcon = (isReopened) =>
  L.divIcon({
    className: "road-closure-marker",
    html: `
      <div style="
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${isReopened ? "#16a34a" : "#dc2626"};
        border: 2px solid #fff;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 2px 6px rgba(0,0,0,0.35);
      ">
        <span style="transform: rotate(45deg); font-size: 13px; line-height: 1;">${
          isReopened ? "✅" : "🚧"
        }</span>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30],
  });

const isReopened = (closure) =>
  Boolean(closure?.roadblockEnd) &&
  moment(closure.roadblockEnd).isBefore(moment());

const formatDateTime = (value) =>
  value ? moment(value, "YYYY-MM-DD HH:mm:ss").format("YYYY-MM-DD hh:mm A") : "N/A";

const PopupRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <span>
      <span style={{ fontWeight: 600 }}>{label}:</span> {value}
      <br />
    </span>
  );
};

const RoadClosureMap = () => {
  const [
    fetchRoadClosures,
    { data: closureRes, isLoading, isFetching },
  ] = useLazyGetRoadClosureMapDataQuery();

  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapContainerRef = useRef(null);

  useEffect(() => {
    fetchRoadClosures({});
  }, [fetchRoadClosures]);

  const closures = useMemo(() => closureRes?.data ?? [], [closureRes]);

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      mapContainerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleRefresh = () => fetchRoadClosures({ forceRefresh: true });

  return (
    <Box
      ref={mapContainerRef}
      sx={{
        mt: 2,
        width: isFullscreen ? "100vw" : "100%",
        height: isFullscreen ? "100vh" : "387px",
        position: "relative",
        borderRadius: 2,
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "10px",
          right: "10px",
          zIndex: 1300,
          background: "rgba(0, 0, 0, 0.6)",
          color: "#fff",
          px: 1,
          borderRadius: 54,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <Typography variant="body2" sx={{ fontSize: "12px" }}>
          Nepal Road Closure Map
        </Typography>
        <IconButton onClick={toggleFullscreen} color="inherit" size="small">
          {isFullscreen ? (
            <FullscreenExitIcon fontSize="small" />
          ) : (
            <FullscreenIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: "10px",
          left: "10px",
          zIndex: 1300,
          display: "flex",
          gap: 0.5,
          flexWrap: "wrap",
        }}
      >
        <Chip
          size="small"
          label={`${closures.length} closures`}
          sx={{ background: "rgba(0, 0, 0, 0.6)", color: "#fff" }}
        />
        {closureRes?.fetchedAt && (
          <Chip
            size="small"
            label={`Updated ${moment(closureRes.fetchedAt).fromNow()}`}
            title={`Data cached for 6 hours (fetched ${moment(
              closureRes.fetchedAt
            ).format("YYYY-MM-DD hh:mm A")})`}
            sx={{ background: "rgba(0, 0, 0, 0.6)", color: "#fff" }}
          />
        )}
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress />
          <Typography variant="body2" ml={2}>
            Loading road closure data...
          </Typography>
        </Box>
      ) : (
        <MapContainer
          center={NEPAL_CENTER}
          zoom={7}
          style={{ width: "100%", height: "100%", borderRadius: 10 }}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          {closures.map(
            (closure) =>
              Number.isFinite(closure.latitude) &&
              Number.isFinite(closure.longitude) && (
                <Marker
                  key={closure.id}
                  position={[closure.latitude, closure.longitude]}
                  icon={buildClosureIcon(isReopened(closure))}
                >
                  <Popup
                    style={{
                      fontSize: "7px",
                      lineHeight: "1.2",
                      padding: "4px 5px",
                      minWidth: "150px",
                      maxWidth: "180px",
                      background: "#fff",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      color: "#555",
                      margin: "-10px",
                    }}
                  >
                    <strong style={{ color: "#222" }}>
                      🚧 {closure.roadName || "Unnamed Road"}
                    </strong>
                    <hr style={{ border: "0", height: "1px", background: "#ddd", margin: "4px 0" }} />
                    <PopupRow label="Road" value={[closure.roadRefNo, closure.linkCode].filter(Boolean).join(" / ")} />
                    <PopupRow label="Status" value={isReopened(closure) ? "Reopened" : closure.closureType || "CLOSED"} />
                    <PopupRow label="Reason" value={closure.closureReason} />
                    <PopupRow label="District" value={closure.district} />
                    <PopupRow label="Division" value={closure.division} />
                    <PopupRow label="Location" value={closure.location} />
                    <PopupRow label="Chainage" value={closure.endChainage ? `${closure.chainage} – ${closure.endChainage}` : closure.chainage} />
                    <PopupRow label="Closed since" value={formatDateTime(closure.roadblockStart)} />
                    {isReopened(closure) ? (
                      <PopupRow label="Reopened" value={formatDateTime(closure.roadblockEnd)} />
                    ) : (
                      <PopupRow label="Est. reopening" value={formatDateTime(closure.roadblockEndEstimated)} />
                    )}
                    <PopupRow label="Repair ETA" value={closure.repairEta} />
                    <PopupRow label="Contact" value={closure.contactPerson} />
                    <PopupRow label="Efforts" value={closure.effortsBeingMade} />
                    <PopupRow label="Remarks" value={closure.remarks} />
                  </Popup>
                </Marker>
              )
          )}
        </MapContainer>
      )}
    </Box>
  );
};

export default RoadClosureMap;
